import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import invoiceService from '../../../services/invoiceService';

const InvoiceForm = ({ vendorId, onSuccess, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    tax_rate: 0,
    discount_amount: 0,
    notes: '',
    terms_and_conditions: 'Payment due within 30 days. Late payments subject to 1.5% monthly fee.',
    status: 'draft'
  });

  const [items, setItems] = useState([
    { description: '', quantity: 1, unit_price: 0 }
  ]);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      if (!vendorId) return;

      try {
        const result = await invoiceService.getVendorTemplates(vendorId);
        if (result?.success) {
          setTemplates(result.data || []);
        }
      } catch (error) {
        console.log('Error loading templates:', error);
      }
    };

    loadTemplates();
  }, [vendorId]);

  // Set due date to 30 days from invoice date by default
  useEffect(() => {
    if (formData?.invoice_date) {
      const dueDate = new Date(formData.invoice_date);
      dueDate.setDate(dueDate.getDate() + 30);
      setFormData(prev => ({
        ...prev,
        due_date: dueDate.toISOString().split('T')[0]
      }));
    }
  }, [formData?.invoice_date]);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    setItems(prev => prev?.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Add new item
  const addItem = () => {
    setItems(prev => [...prev, { description: '', quantity: 1, unit_price: 0 }]);
  };

  // Remove item
  const removeItem = (index) => {
    if (items?.length > 1) {
      setItems(prev => prev?.filter((_, i) => i !== index));
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = items?.reduce((sum, item) => {
      return sum + (parseFloat(item?.quantity || 0) * parseFloat(item?.unit_price || 0));
    }, 0);

    const discount = parseFloat(formData?.discount_amount || 0);
    const taxableAmount = subtotal - discount;
    const tax = taxableAmount * (parseFloat(formData?.tax_rate || 0) / 100);
    const total = subtotal - discount + tax;

    return {
      subtotal: subtotal.toFixed(2),
      discount: discount.toFixed(2),
      tax: tax.toFixed(2),
      total: total.toFixed(2)
    };
  };

  // Load template
  const loadTemplate = async (templateId) => {
    if (!templateId) return;

    try {
      const template = templates?.find(t => t?.id === templateId);
      if (template) {
        setFormData(prev => ({
          ...prev,
          terms_and_conditions: template?.default_terms || prev.terms_and_conditions,
          notes: template?.default_notes || prev.notes,
          tax_rate: template?.default_tax_rate || prev.tax_rate
        }));

        if (template?.invoice_template_items?.length > 0) {
          setItems(template.invoice_template_items.map(item => ({
            description: item?.description || '',
            quantity: 1,
            unit_price: parseFloat(item?.unit_price || 0)
          })));
        }
      }
    } catch (error) {
      console.log('Error loading template:', error);
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    loadTemplate(templateId);
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData?.client_name?.trim()) {
        throw new Error('Client name is required');
      }

      if (items?.some(item => !item?.description?.trim())) {
        throw new Error('All items must have a description');
      }

      const totals = calculateTotals();

      // Create invoice
      const invoiceData = {
        ...formData,
        subtotal: parseFloat(totals.subtotal),
        tax_amount: parseFloat(totals.tax),
        total_amount: parseFloat(totals.total)
      };

      const result = await invoiceService.createInvoice(vendorId, invoiceData);

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create invoice');
      }

      // Add items to invoice
      const invoice = result.data;
      for (let i = 0; i < items?.length; i++) {
        const item = items[i];
        if (item?.description?.trim()) {
          await invoiceService.addInvoiceItem(invoice.id, {
            description: item.description,
            quantity: parseInt(item.quantity || 1),
            unit_price: parseFloat(item.unit_price || 0),
            sort_order: i
          });
        }
      }

      // Get updated invoice with items
      const updatedResult = await invoiceService.getInvoiceById(invoice.id);
      if (updatedResult?.success) {
        onSuccess?.(updatedResult.data);
      } else {
        onSuccess?.(invoice);
      }

    } catch (error) {
      setError(error?.message || 'Failed to create invoice');
      console.log('Error creating invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={20} className="text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Template Selection */}
      {templates?.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Use Template (Optional)
          </label>
          <select
            value={selectedTemplate}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
          >
            <option value="">Select a template...</option>
            {templates?.map((template) => (
              <option key={template?.id} value={template?.id}>
                {template?.template_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Client Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Client Name *
          </label>
          <input
            type="text"
            required
            value={formData?.client_name}
            onChange={(e) => handleFieldChange('client_name', e.target.value)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            placeholder="Enter client name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Client Email
          </label>
          <input
            type="email"
            value={formData?.client_email}
            onChange={(e) => handleFieldChange('client_email', e.target.value)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            placeholder="Enter client email"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Client Phone
          </label>
          <input
            type="tel"
            value={formData?.client_phone}
            onChange={(e) => handleFieldChange('client_phone', e.target.value)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            placeholder="Enter client phone"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Invoice Date
          </label>
          <input
            type="date"
            required
            value={formData?.invoice_date}
            onChange={(e) => handleFieldChange('invoice_date', e.target.value)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Due Date
          </label>
          <input
            type="date"
            required
            value={formData?.due_date}
            onChange={(e) => handleFieldChange('due_date', e.target.value)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
          />
        </div>
      </div>

      {/* Client Address */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Client Address
        </label>
        <textarea
          value={formData?.client_address}
          onChange={(e) => handleFieldChange('client_address', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
          placeholder="Enter client address"
        />
      </div>

      {/* Invoice Items */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-lg text-text-primary">Invoice Items</h3>
          <Button type="button" variant="outline" size="sm" iconName="Plus" onClick={addItem}>
            Add Item
          </Button>
        </div>

        <div className="space-y-4">
          {items?.map((item, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-border-light rounded-lg">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Description *
                </label>
                <input
                  type="text"
                  required
                  value={item?.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                  placeholder="Item description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={item?.quantity}
                  onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                />
              </div>

              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Unit Price
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item?.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                {items?.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => removeItem(index)}
                    className="text-red-600 hover:text-red-700"
                  />
                )}
              </div>

              <div className="md:col-span-4 text-right">
                <span className="text-sm text-text-muted">Total: </span>
                <span className="font-medium text-text-primary">
                  ${((item?.quantity || 1) * (item?.unit_price || 0)).toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tax and Discount */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Tax Rate (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={formData?.tax_rate}
            onChange={(e) => handleFieldChange('tax_rate', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Discount Amount ($)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData?.discount_amount}
            onChange={(e) => handleFieldChange('discount_amount', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
          />
        </div>
      </div>

      {/* Invoice Totals */}
      <div className="bg-surface-50 p-4 rounded-lg">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-text-secondary">Subtotal:</span>
            <span className="font-medium">${totals.subtotal}</span>
          </div>
          {parseFloat(totals.discount) > 0 && (
            <div className="flex justify-between">
              <span className="text-text-secondary">Discount:</span>
              <span className="font-medium text-red-600">-${totals.discount}</span>
            </div>
          )}
          {parseFloat(totals.tax) > 0 && (
            <div className="flex justify-between">
              <span className="text-text-secondary">Tax ({formData?.tax_rate}%):</span>
              <span className="font-medium">${totals.tax}</span>
            </div>
          )}
          <div className="border-t border-border-light pt-2">
            <div className="flex justify-between text-lg font-bold">
              <span>Total:</span>
              <span className="text-primary">${totals.total}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes and Terms */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Notes
          </label>
          <textarea
            value={formData?.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            placeholder="Additional notes for this invoice"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Terms and Conditions
          </label>
          <textarea
            value={formData?.terms_and_conditions}
            onChange={(e) => handleFieldChange('terms_and_conditions', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            placeholder="Payment terms and conditions"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4 pt-6 border-t border-border-light">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="flex-1"
        >
          {loading ? 'Creating...' : 'Create Invoice'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default InvoiceForm;